import React, { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, Lock, AlertCircle } from "lucide-react";
import { usePublicAccess } from "../../context/PublicAccessContext";
import PublicAccessService from "../../services/PublicAccessService";
import "./PasswordGate.css";

const PasswordGate = ({ children }) => {
  const { accessToken, saveToken } = usePublicAccess();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!accessToken && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;

    setIsLoading(true);
    setError("");

    try {
      const data = await PublicAccessService.verify(password);
      if (data?.success && data?.accessToken) {
        saveToken(data.accessToken);
      } else {
        setError("Invalid password. Please try again.");
        setPassword("");
        inputRef.current?.focus();
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("Invalid password. Please try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setPassword("");
      inputRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  if (accessToken) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Page content blurred behind modal */}
      <div className="pg-backdrop" aria-hidden="true" />

      <div className="pg-modal-wrap" role="dialog" aria-modal="true" aria-labelledby="pg-title">
        <div className="pg-modal">
          {/* Header */}
          <div className="pg-modal-header">
            <div className="pg-lock-icon">
              <Lock size={20} />
            </div>
            <div>
              <h2 id="pg-title" className="pg-modal-title">Intelligence Offshore</h2>
              <p className="pg-modal-subtitle">Enter your password to access this site</p>
            </div>
          </div>

          {/* Divider */}
          <hr className="pg-divider" />

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="pg-field">
              <label htmlFor="pg-password" className="pg-label">Password</label>
              <div className={`pg-input-wrap${error ? " pg-input-wrap--error" : ""}`}>
                <input
                  id="pg-password"
                  ref={inputRef}
                  type={showPassword ? "text" : "password"}
                  className="pg-input"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError("");
                  }}
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="pg-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={0}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {error && (
                <p id="pg-error" className="pg-error" role="alert">
                  <AlertCircle size={13} />
                  {error}
                </p>
              )}
            </div>

            <button
              id="pg-submit-btn"
              type="submit"
              className="pg-submit-btn"
              disabled={isLoading || !password.trim()}
            >
              {isLoading ? (
                <>
                  <span className="pg-spinner" />
                  Verifying…
                </>
              ) : (
                "Unlock Access"
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default PasswordGate;
